import { useMemo } from 'react';
import { batch } from 'react-redux';
import isNil from 'lodash/isNil';
import {
  selectChartTypeAction,
  setChartSettingsTableSettingsAction,
} from '../../../redux/slices/analyses';
import { isSkipPercentView, getIconFromTypeInChartSelector } from '../utils';
import { CHART_TYPES } from '../constants/chartTypes';
import {
  ExpressionModeKey,
  Geo,
  QueryType,
} from '../../../utils/apollo/graphqlSchema';
import { useCurrentUrl } from '../../../utils/routing';
import {
  chartTypesNotSupportComparison,
  chartTypesRequiredBreakdown,
  geoMapChartType,
  regularExpressionChartTypes,
  regularFunnelChartTypes,
  regularRetentionChartTypes,
  tableChartType,
} from './utils';
import { OVERALL_SLICE_KEY } from '../constants/utils';
import { useAppDispatch } from '../../../redux/hooks';
import { useChartContext } from '../ChartContext';
import { SingleSelectMenu } from '../../Dropdown';
import { ISingleSelectMenuOption } from '../../Dropdown/SingleSelectMenu/types';
import { ChartViewOptions } from '../../../redux/utils/chartSettings';

const extendOptionsWithIcon = (option: ISingleSelectMenuOption) => ({
  ...option,
  iconName: getIconFromTypeInChartSelector[option.value as CHART_TYPES],
});

export default () => {
  const dispatch = useAppDispatch();
  const {
    metadata: { analysisId, geo, comparisonInfo, query, queryType },
    chartData,
    settings: { chartSettings },
  } = useChartContext();

  const divisionFormula =
    chartData.queryType === QueryType.Expression
      ? isSkipPercentView(query, chartData.measureControls)
      : false;

  const { isDashboardInUrl, isWorkspacesInUrl, isDebugInUrl } = useCurrentUrl();
  const { chartType, displayedSliceSettings } = chartSettings;

  // if overall is the only group, it means no breakdown to display the values.
  const hasBreakdown =
    (query.__typename === 'ExpressionQuery' &&
      query.expressionMode.key === ExpressionModeKey.Histogram) ||
    ('breakdown' in query &&
      (query.breakdown?.groupbys?.length ?? 0) +
        (query.breakdown?.cohortRefs?.length ?? 0) >
        0);

  // if overall group not exists, it is old chart data that has breakdown but no overall group
  // then we need to disable chart types that require overall group
  const isDisableChartTypesRequiredOverall =
    hasBreakdown &&
    !displayedSliceSettings.some(
      (sliceSetting) => sliceSetting.key === OVERALL_SLICE_KEY,
    );

  const shouldAppendDataTableType =
    chartData.queryType === QueryType.Expression &&
    (isDashboardInUrl || isWorkspacesInUrl || isDebugInUrl);

  const chartTypeOptions: ISingleSelectMenuOption[] = useMemo(() => {
    if (queryType === QueryType.Expression) {
      const expressionChartTypes = [...regularExpressionChartTypes];
      if (shouldAppendDataTableType) {
        expressionChartTypes.push(tableChartType);
      }
      if (geo !== Geo.Na) {
        expressionChartTypes.unshift(geoMapChartType);
      }

      if (
        'expressionMode' in query &&
        (query.expressionMode.key === ExpressionModeKey.Query ||
          query.expressionMode.key === ExpressionModeKey.Histogram)
      ) {
        const isChartTypeDisabled =
          !hasBreakdown || isDisableChartTypesRequiredOverall;

        // show disabled chart types in different cases:
        // 1. no slices other than overall, it means no breakdown to display the values.
        // 2. isDisableChartTypesRequiredOverall, old charts doesn't support new chart types.
        expressionChartTypes.push(
          ...chartTypesRequiredBreakdown.map((chartTypeObj) => {
            // KBT-987: disable options that doesn't support compare mode
            if (
              !isNil(comparisonInfo?.comparisonConfig) &&
              chartTypesNotSupportComparison.includes(
                chartTypeObj.value as CHART_TYPES,
              )
            ) {
              return {
                ...chartTypeObj,
                disabled: true,
                tooltipOption: {
                  content: 'Disable compare to use chart type.',
                },
              } satisfies ISingleSelectMenuOption;
            }

            // when division formula, kobol cannot return percentage data
            // therefore Pie chart is not supported
            if (
              divisionFormula &&
              chartTypeObj.value === CHART_TYPES.PIE_CHART
            ) {
              return {
                ...chartTypeObj,
                disabled: true,
                tooltipOption: {
                  content:
                    'Pie chart is not supported with division formula or non-countable measure function.',
                },
              } satisfies ISingleSelectMenuOption;
            }

            return {
              ...chartTypeObj,
              disabled: isChartTypeDisabled,
              tooltipOption: isChartTypeDisabled
                ? !hasBreakdown
                  ? chartTypeObj.tooltipOption
                  : {
                      content:
                        'To enable this visualization, please re-execute it to fetch new data.',
                    }
                : undefined,
            } satisfies ISingleSelectMenuOption;
          }),
        );
      }

      // if visible measures more than 1 or equal to 0,
      // we only display Line Chart and Bar Chart
      return expressionChartTypes.map(extendOptionsWithIcon);
    } else if (queryType === QueryType.Funnel) {
      return regularFunnelChartTypes.map(extendOptionsWithIcon);
    } else if (queryType === QueryType.Retention) {
      return regularRetentionChartTypes.map(extendOptionsWithIcon);
    } else {
      return [];
    }
  }, [
    query,
    queryType,
    shouldAppendDataTableType,
    divisionFormula,
    geo,
    hasBreakdown,
    isDisableChartTypesRequiredOverall,
    comparisonInfo,
  ]);

  const onChartTypeChange = (selectedType: string) => {
    const newType = selectedType as CHART_TYPES;

    batch(() => {
      // only for query types that contain table view.
      // currently only for expression
      if (shouldAppendDataTableType) {
        dispatch(
          setChartSettingsTableSettingsAction({
            analysisId,
            tableSettings: {
              chartView:
                newType === CHART_TYPES.EXPRESSION_TABLE
                  ? ChartViewOptions.Table
                  : ChartViewOptions.ChartAndTable,
            },
          }),
        );
      }

      dispatch(selectChartTypeAction({ analysisId, chartType: newType }));
    });
  };

  if (chartTypeOptions.length === 0) {
    return null;
  }

  return (
    <SingleSelectMenu
      options={chartTypeOptions}
      value={chartType}
      onChange={onChartTypeChange}
      menuMaxHeight="auto"
      isLabelWithIcon={true}
    />
  );
};
